/**
 * WelcomeTour — Resmo-guided walkthrough of the key features, shown on the
 * Welcome page when onboarding set `resmo:welcome_tour:pending`. A calm
 * modal carousel (no element spotlighting) with a "Take me there" jump on
 * each step. Dismissible any time; never re-shows once closed.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ResmoMascot from "./ResmoMascot";
import { X, ArrowRight, ArrowLeft, Search, Wand2, ShieldCheck, Mic, Coins, Footprints, FileText, Layers } from "lucide-react";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";

const TOUR_STEPS = [
  { icon: FileText, title: "Feed me your resume", blurb: "Everything starts here. I read your resume once and power every tool with it — no re-typing, ever.", route: "/feed-resume" },
  { icon: Search, title: "Live job search + AI matches", blurb: "Real openings from the live feed, scored against YOUR resume — with remote and salary filters that remember your preferences.", route: "/job-search" },
  { icon: Wand2, title: "Resume Studio with ATS help", blurb: "Tailor your resume for any job, watch your ATS score climb, and download a polished DOCX or PDF — then apply in one click.", route: "/studio" },
  { icon: ShieldCheck, title: "Authenticity check", blurb: "My integrity check keeps every edit honest — nothing invented, nothing you can't back up in an interview.", route: "/studio" },
  { icon: Layers, title: "Briefings that provide insights", blurb: "Job-fit reports, strategy briefs and opportunity decoders that tell you the TRUE problem a role is solving for.", route: "/feed-job" },
  { icon: Mic, title: "Interview playbooks", blurb: "Role-specific playbooks, question banks and mock interviews — walk in knowing what they'll ask and why.", route: "/playbooks" },
  { icon: Coins, title: "What your skills are worth", blurb: "A grounded salary read based on your actual experience — plus the elevator speech creator for networking events.", route: "/skills" },
  { icon: Footprints, title: "Lessons learned & stepping stones", blurb: "After every application and interview, I capture what worked so each attempt is smarter than the last.", route: "/stepping-stones" },
];

export default function WelcomeTour() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("resmo:welcome_tour:pending") === "1"; } catch { return false; }
  });
  const [step, setStep] = useState(0);

  const close = () => {
    setOpen(false);
    try { localStorage.removeItem("resmo:welcome_tour:pending"); } catch { /* ignore */ }
  };

  if (!open) return null;
  const s = TOUR_STEPS[step];
  const Icon = s.icon;
  const last = step === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-4"
         style={{ background: "rgba(15,27,45,0.72)", backdropFilter: "blur(3px)" }}
         data-testid="welcome-tour">
      <div className="relative w-full max-w-md p-6 text-center"
           style={{ background: "white", border: `3px solid ${NAVY}`, borderRadius: 14, boxShadow: `6px 6px 0 0 ${ORANGE}` }}>
        <button onClick={close} className="absolute top-3 right-3 opacity-60 hover:opacity-100"
                style={{ background: "none", border: "none", cursor: "pointer", color: NAVY }}
                aria-label="Close tour" data-testid="welcome-tour-close">
          <X size={18} />
        </button>
        <div className="flex justify-center"><ResmoMascot state="pointing" size={96} /></div>
        <div className="font-mono-rm text-[10px] uppercase tracking-widest font-bold mt-2" style={{ color: ORANGE }}
             data-testid="welcome-tour-progress">
          Tour · {step + 1} of {TOUR_STEPS.length}
        </div>
        <h3 className="font-display text-xl md:text-2xl uppercase mt-1 flex items-center justify-center gap-2"
            style={{ color: NAVY }} data-testid="welcome-tour-title">
          <Icon size={20} style={{ color: ORANGE }} /> {s.title}
        </h3>
        <p className="text-sm mt-2" style={{ color: "#475569" }} data-testid="welcome-tour-blurb">{s.blurb}</p>
        <button onClick={() => { close(); navigate(s.route); }}
                className="text-xs font-bold mt-3 hover:underline"
                style={{ color: "#B45309", background: "none", border: "none", cursor: "pointer" }}
                data-testid="welcome-tour-goto">
          Take me there →
        </button>
        <div className="flex items-center justify-between mt-5">
          <button onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0}
                  className="rm-btn rm-btn-ghost text-xs disabled:opacity-40" data-testid="welcome-tour-back">
            <ArrowLeft size={13} /> Back
          </button>
          <div className="flex gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <span key={i} className="rounded-full" style={{ width: i === step ? 16 : 6, height: 6, background: i <= step ? ORANGE : "#CBD5E1" }} />
            ))}
          </div>
          {last ? (
            <button onClick={close} className="rm-btn rm-btn-primary text-xs" data-testid="welcome-tour-done">
              Done — let's go!
            </button>
          ) : (
            <button onClick={() => setStep((v) => v + 1)} className="rm-btn rm-btn-primary text-xs" data-testid="welcome-tour-next">
              Next <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
